import { useParams, Navigate, Link } from "react-router-dom";
import {
  Clock,
  MonitorSmartphone,
  Wallet,
  Check,
  ArrowUpRight,
  MessageCircle,
  Phone,
  Award,
  Compass,
  Info,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { Reveal, StaggerGroup, StaggerItem, MaskedLines } from "@/components/shared/Reveal";
import { RequestCallbackDialog } from "@/components/shared/RequestCallbackDialog";
import { EnquiryForm } from "@/components/shared/EnquiryForm";
import { useSeo } from "@/hooks/useSeo";
import { getProgram, getRelated, detailPath, waLinkFor } from "@/lib/programsData";

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
    <Icon className="h-5 w-5 text-orange" />
    <div className="flex flex-col leading-tight">
      <span className="text-[11px] uppercase tracking-wider text-white/45">{label}</span>
      <span className="text-sm font-semibold text-white">{value}</span>
    </div>
  </div>
);

const Block = ({ title, children }) => (
  <Reveal className="flex flex-col gap-5">
    <h2 className="font-heading text-2xl text-navy md:text-3xl">{title}</h2>
    {children}
  </Reveal>
);

const CheckList = ({ items }) => (
  <ul className="grid gap-3 sm:grid-cols-2">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-3 text-navy/75">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-royal/10 text-royal">
          <Check className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm">{it}</span>
      </li>
    ))}
  </ul>
);

export default function ProgramDetail({ category }) {
  const { slug } = useParams();
  const program = getProgram(slug);
  const valid = program && program.category === category;

  const isInternship = category === "internship";
  const listingPathX = isInternship ? "/internships" : "/programs";
  const listingLabelX = isInternship ? "Internships" : "Programs";

  useSeo(
    valid
      ? {
          title: program.name,
          description: `${program.name} at ASAI. ${program.tagline} Fee: ${program.fee}. Mode: ${program.mode}. Duration: ${program.duration}.`,
          path: `${listingPathX}/${slug}`,
          breadcrumbs: [
            { name: "Home", path: "/" },
            { name: listingLabelX, path: listingPathX },
            { name: program.name, path: `${listingPathX}/${slug}` },
          ],
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Course",
            name: program.name,
            description: program.overview,
            provider: {
              "@type": "EducationalOrganization",
              name: "ASAI – Atmasamman School of AI",
              sameAs: "https://www.atmasammanschoolofai.com",
            },
            educationalLevel: "Professional",
            timeRequired: program.duration,
            offers: program.isFree
              ? {
                  "@type": "Offer",
                  price: "0",
                  priceCurrency: "INR",
                  availability: "https://schema.org/LimitedAvailability",
                  description: "Free for selected candidates only.",
                }
              : {
                  "@type": "Offer",
                  price: (program.fee || "").replace(/[^\d]/g, "") || undefined,
                  priceCurrency: "INR",
                  availability: "https://schema.org/InStock",
                },
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: program.mode,
              location: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "Bulandshahr",
                  addressRegion: "Uttar Pradesh",
                  addressCountry: "IN",
                },
              },
            },
          },
        }
      : { title: isInternship ? "Internships" : "Programs" }
  );

  // Guard: unknown slug or wrong category for this route
  if (!valid) {
    return <Navigate to={category === "internship" ? "/internships" : "/programs"} replace />;
  }

  const {
    name, tagline, overview, whoShouldJoin, eligibility, outcomes, curriculum,
    duration, mode, fee, feeNote, isFree, selectionNote, certification, careerSupport, faqs,
  } = program;

  const related = getRelated(program.related);
  const listingPath = category === "internship" ? "/internships" : "/programs";
  const listingLabel = category === "internship" ? "Internships" : "Programs";

  return (
    <div data-testid={`program-detail-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-16 pt-32 md:pb-20 md:pt-40">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(45% 55% at 10% 5%, hsla(224,76%,48%,0.35), transparent), radial-gradient(40% 45% at 95% 25%, hsla(21,100%,50%,0.16), transparent)",
          }}
        />
        <div className="container-asai relative z-10">
          <nav className="mb-6 flex items-center gap-2 text-xs text-white/50" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to={listingPath} className="hover:text-white">{listingLabel}</Link>
            <span>/</span>
            <span className="text-white/80">{name}</span>
          </nav>

          <div className="grid gap-10 lg:grid-cols-[1.3fr_0.7fr] lg:items-start">
            <div>
              {isFree && (
                <span className="mb-4 inline-block rounded-full bg-orange px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
                  Free for Selected Candidates
                </span>
              )}
              <h1 className="font-heading text-4xl font-bold leading-[0.98] tracking-tight text-white md:text-6xl">
                <MaskedLines lines={[name]} />
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-white/70">{tagline}</p>

              <div className="mt-8 flex flex-wrap gap-3">
                <PrimaryButton to="/apply" withArrow testId="detail-apply">Apply Now</PrimaryButton>
                <RequestCallbackDialog
                  programName={name}
                  testId="detail-callback"
                  trigger={
                    <button data-testid="detail-callback" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white hover:text-navy">
                      <Phone className="h-4 w-4" /> Request Callback
                    </button>
                  }
                />
                <a href={waLinkFor(name)} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-medium text-white transition-transform hover:scale-105">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>

            <div className="grid gap-3">
              <Meta icon={Clock} label="Duration" value={duration} />
              <Meta icon={MonitorSmartphone} label="Mode" value={mode} />
              <Meta icon={Wallet} label="Fee" value={`${fee}${feeNote ? " · " + feeNote : ""}`} />
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-asai flex flex-col gap-16">
          {isFree && selectionNote && (
            <Reveal className="flex items-start gap-4 rounded-2xl border border-orange/30 bg-orange/5 p-6">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
              <p className="text-sm text-navy/80"><span className="font-semibold">Selection note:</span> {selectionNote}</p>
            </Reveal>
          )}

          <Block title="Overview">
            <p className="max-w-3xl text-lg leading-relaxed text-navy/70">{overview}</p>
          </Block>

          <div className="grid gap-16 md:grid-cols-2">
            <Block title="Who should join"><CheckList items={whoShouldJoin} /></Block>
            <Block title="Eligibility"><CheckList items={eligibility} /></Block>
          </div>

          <Block title="Learning outcomes"><CheckList items={outcomes} /></Block>

          <Block title="Curriculum">
            <StaggerGroup className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {curriculum.map((c, i) => (
                <StaggerItem key={c} className="flex items-center gap-3 rounded-xl border border-navy/10 bg-lightgrey px-4 py-3">
                  <span className="font-heading text-sm font-bold text-royal">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-medium text-navy">{c}</span>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </Block>

          <div className="grid gap-6 md:grid-cols-2">
            <Reveal className="flex flex-col gap-4 rounded-2xl border border-navy/10 bg-lightgrey p-8">
              <Award className="h-8 w-8 text-royal" />
              <h3 className="font-heading text-xl text-navy">Certification</h3>
              <p className="text-sm text-navy/70">{certification}</p>
            </Reveal>
            <Reveal delay={0.1} className="flex flex-col gap-4 rounded-2xl border border-navy/10 bg-lightgrey p-8">
              <Compass className="h-8 w-8 text-royal" />
              <h3 className="font-heading text-xl text-navy">Career Support</h3>
              <p className="text-sm text-navy/70">{careerSupport}</p>
            </Reveal>
          </div>

          <Block title="Frequently asked questions">
            <Accordion type="single" collapsible className="w-full max-w-3xl" data-testid="detail-faqs">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-heading text-lg text-navy">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-navy/70">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Block>
        </div>
      </section>

      {/* ENQUIRY */}
      <section className="bg-navy py-20 md:py-28" data-testid="detail-enquiry">
        <div className="container-asai grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <SectionHeading onDark overline="Enquire" title="Interested in this program?" description="Request a callback and our team will help you with the details and next steps." />
          <EnquiryForm onDark source={`program:${slug}`} defaultCourse={name} testId="detail-enquiry-form" />
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-16">
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, hsl(224 76% 40%), hsl(21 100% 50%))" }} />
        <div className="container-asai relative z-10 flex flex-col items-center gap-6 text-center">
          <h2 className="font-heading text-3xl font-bold text-white md:text-4xl">Ready to join {name}?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <PrimaryButton to="/apply" size="lg" className="bg-white !text-navy hover:!bg-white" withArrow testId="detail-cta-apply">Apply Now</PrimaryButton>
            <RequestCallbackDialog
              programName={name}
              testId="detail-cta-callback"
              trigger={
                <button data-testid="detail-cta-callback" className="inline-flex items-center gap-2 rounded-full border border-white/50 px-8 py-4 text-base font-medium text-white transition-colors hover:bg-white/10">
                  <Phone className="h-4 w-4" /> Request Callback
                </button>
              }
            />
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-navy py-20 md:py-24">
          <div className="container-asai">
            <SectionHeading onDark overline="Keep exploring" title="Related programs" />
            <StaggerGroup className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <StaggerItem key={r.slug}>
                  <Link to={detailPath(r)} data-testid={`related-${r.slug}`} className="group flex h-full flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-all hover:-translate-y-1 hover:border-orange/50">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange">
                      <Clock className="h-3.5 w-3.5" /> {r.duration}
                    </div>
                    <h3 className="font-heading text-xl text-white">{r.name}</h3>
                    <p className="text-sm text-white/60">{r.homeSummary}</p>
                    <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-orange">
                      View details <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </section>
      )}
    </div>
  );
}
